import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, Inject, Input, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { SessionService } from '../../../services/session.service';
import { BackendMenuJsonItem, SidebarMenuItem } from './sidebar.interfaces';

const DASHBOARD_MENU_ROUTE = '/dashboard';
const DASHBOARD_LANDING_ROUTES = new Set([DASHBOARD_MENU_ROUTE, '/admin-dashboard']);

@Component({
  selector: 'app-dashboard-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class DashboardSidebarComponent implements OnInit {
  @Input() collapsed = false;

  protected readonly items = signal<SidebarMenuItem[]>([]);
  protected readonly activeRoute = signal('');
  protected readonly openMenuIds = signal<number[]>([]);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly sessionSvc: SessionService,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnInit(): void {
    this.loadMenuItems();
    this.syncActiveRoute(this.router.url);

    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((event) => {
          this.syncActiveRoute(event.urlAfterRedirects);
        });
    }
  }

  protected toggleGroup(itemId: number): void {
    const currentOpenIds = this.openMenuIds();
    if (currentOpenIds.includes(itemId)) {
      this.openMenuIds.set(currentOpenIds.filter((id) => id !== itemId));
      return;
    }

    this.openMenuIds.set([itemId]);
  }

  protected isOpen(itemId: number): boolean {
    return this.openMenuIds().includes(itemId);
  }

  protected isActive(item: SidebarMenuItem): boolean {
    if (item.route === DASHBOARD_MENU_ROUTE) {
      return DASHBOARD_LANDING_ROUTES.has(this.activeRoute());
    }

    return this.activeRoute() === item.route || this.hasActiveDescendant(item);
  }

  protected handleItemClick(item: SidebarMenuItem): void {
    if (item.children.length > 0) {
      this.toggleGroup(item.id);
      return;
    }

    if (item.route) {
      this.router.navigateByUrl(item.route);
    }
  }

  protected trackById(_: number, item: SidebarMenuItem): number {
    return item.id;
  }

  private loadMenuItems(): void {
    const menuJson = this.sessionSvc.getMenuJson() as BackendMenuJsonItem[] | null;
    this.items.set(this.buildMenuTree(menuJson));
  }

  private buildMenuTree(menuJson: BackendMenuJsonItem[] | null): SidebarMenuItem[] {
    if (!Array.isArray(menuJson) || menuJson.length === 0) {
      return [];
    }

    const sortedMenu = [...menuJson]
      .filter((item) => item.is_read)
      .sort((left, right) => {
        const leftParent = left.parent_menu_id ?? Number.NEGATIVE_INFINITY;
        const rightParent = right.parent_menu_id ?? Number.NEGATIVE_INFINITY;

        if (leftParent !== rightParent) {
          return leftParent - rightParent;
        }

        const leftOrder = left.order_by ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.order_by ?? Number.MAX_SAFE_INTEGER;

        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        return (left.menu_name ?? '').localeCompare(right.menu_name ?? '');
      });

    const menuNodes = new Map<number, SidebarMenuItem>();

    for (const item of sortedMenu) {
      menuNodes.set(item.id, {
        id: item.id,
        label: item.menu_name ?? item.menu_code ?? 'Menu',
        iconClass: item.icon_class ?? 'fa-circle',
        route: this.normalizeRoute(item.menu_url),
        isRead: item.is_read,
        isWrite: item.is_write,
        parentMenuId: item.parent_menu_id,
        orderBy: item.order_by,
        children: [],
      });
    }

    const roots: SidebarMenuItem[] = [];
    for (const item of sortedMenu) {
      const node = menuNodes.get(item.id);
      if (!node) {
        continue;
      }

      if (item.parent_menu_id === null) {
        roots.push(node);
        continue;
      }

      const parent = menuNodes.get(item.parent_menu_id);
      if (!parent) {
        roots.push(node);
        continue;
      }

      parent.children.push(node);
    }

    return roots.sort((left, right) => (left.orderBy ?? Number.MAX_SAFE_INTEGER) - (right.orderBy ?? Number.MAX_SAFE_INTEGER));
  }

  private normalizeRoute(menuUrl: string | null): string | undefined {
    if (!menuUrl?.trim()) {
      return undefined;
    }

    return menuUrl.startsWith('/') ? menuUrl : `/${menuUrl}`;
  }

  private syncActiveRoute(url: string): void {
    this.activeRoute.set(url.split('?')[0]);

    const activeParentIds = this.findExpandedGroupIds(this.items(), this.activeRoute());
    this.openMenuIds.set(activeParentIds);
  }

  private findExpandedGroupIds(items: SidebarMenuItem[], activeUrl: string): number[] {
    for (const item of items) {
      if (item.children.length === 0) {
        continue;
      }

      if (this.hasActiveDescendant(item, activeUrl)) {
        return [item.id, ...this.findExpandedGroupIds(item.children, activeUrl)];
      }
    }

    return [];
  }

  private hasActiveDescendant(item: SidebarMenuItem, activeUrl = this.activeRoute()): boolean {
    if (item.route === activeUrl) {
      return true;
    }

    return item.children.some((child) => this.hasActiveDescendant(child, activeUrl));
  }
}
